/**
 * ProShip Direct — the active plan, and how much of it is left.
 *
 * The two numbers a merchant on a metered plan checks are "how many have I used" and "how many
 * are left before I start paying per shipment", so those lead. Over-usage is shown as a real
 * rupee figure once it starts, not as a warning they have to translate.
 *
 * Shipment counts are illustrative on this build — usage metering does not exist in the backend
 * (the reviewed branch flags the same gap against its platform fee).
 */
import React from 'react';
import { Alert, Box, Grid, Typography, LinearProgress, Switch, Chip, Button, Tooltip, Divider } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import UpgradeOutlinedIcon from '@mui/icons-material/UpgradeOutlined';

import { sectionCardSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, SUCCESS, WARNING, ERROR, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';
import { formatDate } from '../../../helpers/UtilityHelper';
import { statLabelSx, statValueSx, statSubSx } from './stats';

const smallSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.45 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/** The status chip: what the merchant can do on their own couriers right now. */
const STATUS_CHIP = {
  ACTIVE: { label: 'Active', color: SUCCESS },
  GRACE: { label: 'Renewal due', color: WARNING },
  SUSPENDED: { label: 'Suspended', color: ERROR },
};

export default function PlanUsageCard({
  plan, usage, proship, onAutoRenew, onToggleProship, onViewLog, onChangePlan, onRenewNow, onKeepCurrentPlan,
}) {
  const { used, allowance, remaining, overage, percent, overageCost } = usage;
  const inOverage = overage > 0;
  const chip = STATUS_CHIP[plan.status] || STATUS_CHIP.ACTIVE;
  const renewOn = plan.renewsOn || plan.periodEnd;
  const unpaid = plan.status === 'GRACE' || plan.status === 'SUSPENDED';

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, flexWrap: 'wrap', mb: 1.25 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
          {/* The catalogue stops listing the trial once a merchant has had it, so name it from the plan. */}
          {usage.plan?.name || (plan.trial ? 'Trial' : 'Direct')} plan
        </Typography>
        <Chip
          size='small'
          label={chip.label}
          sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, height: 19, bgcolor: `${chip.color}14`, color: chip.color }}
        />
        {plan.activatedVia === 'ADMIN' && (
          <Tooltip title='Set up for you by the ProShip team'>
            <Chip
              size='small'
              label='Activated by ProShip'
              sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], height: 19, bgcolor: `${PRIMARY_BLUE}14`, color: PRIMARY_BLUE }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {/*
          Offered from the plan card rather than a pricing page, because the moment a merchant
          wants a bigger plan is the moment they are looking at the bar below filling up.
        */}
        <Button
          size='small'
          sx={secondaryBtnSx}
          startIcon={<UpgradeOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={onChangePlan}
        >
          Change plan
        </Button>
        <Button size='small' sx={secondaryBtnSx} startIcon={<HistoryOutlinedIcon sx={{ fontSize: 15 }} />} onClick={onViewLog}>
          Activation log
        </Button>
      </Box>

      {/*
        Renewal unpaid. GRACE still ships on their couriers until the grace date; SUSPENDED does
        not. Either way the way out is the same button, and the daily retry is said out loud so
        "add balance and wait" is a known option too.
      */}
      {unpaid && (
        <Alert
          severity={plan.status === 'SUSPENDED' ? 'error' : 'warning'}
          sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}
          action={onRenewNow && (
            <Button size='small' sx={secondaryBtnSx} onClick={onRenewNow}>
              Pay {plan.renewalTotal != null ? inr(plan.renewalTotal) : 'now'}
            </Button>
          )}
        >
          {plan.status === 'SUSPENDED'
            ? 'Renewal payment pending. Shipping on your own couriers is paused until it is paid. '
            : `Renewal payment pending. Shipping on your own couriers continues until ${plan.graceEndsAt ? formatDate(plan.graceEndsAt, 'dd MMM yyyy') : 'the end of grace'}. `}
          We retry every day; add {plan.paidFrom === 'POSTPAID' ? 'credit' : 'wallet balance'} or pay now.
        </Alert>
      )}

      {plan.nextPlanName && !unpaid && (
        <Alert
          severity='info'
          sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}
          action={onKeepCurrentPlan && (
            <Button size='small' sx={secondaryBtnSx} onClick={onKeepCurrentPlan}>
              Keep {usage.plan?.name || 'current plan'}
            </Button>
          )}
        >
          From {renewOn ? formatDate(renewOn, 'dd MMM yyyy') : 'your renewal'} your plan moves to {plan.nextPlanName}.
        </Alert>
      )}

      <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}>
          <Typography sx={statLabelSx}>Shipments done</Typography>
          <Typography sx={statValueSx}>{used.toLocaleString('en-IN')}</Typography>
          <Typography sx={statSubSx}>on Direct couriers this period</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography sx={statLabelSx}>Balance in plan</Typography>
          <Typography sx={{ ...statValueSx, color: remaining === 0 ? WARNING : NAVY }}>
            {usage.unlimited ? 'Unlimited' : remaining.toLocaleString('en-IN')}
          </Typography>
          <Typography sx={statSubSx}>
            {usage.unlimited ? 'no shipment cap this period' : `of ${allowance.toLocaleString('en-IN')} included`}
          </Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography sx={statLabelSx}>Over-usage rate</Typography>
          <Typography sx={statValueSx}>₹{(usage.plan?.overagePrice || 0).toFixed(2)}</Typography>
          <Typography sx={statSubSx}>per shipment beyond the plan</Typography>
        </Grid>
        <Grid item xs={6} md={3}>
          <Typography sx={statLabelSx}>{plan.trial ? 'Trial ends on' : 'Renews on'}</Typography>
          <Typography sx={{ ...statValueSx, fontSize: SIZE.lg }}>
            {renewOn ? formatDate(renewOn, 'dd MMM yyyy') : '—'}
          </Typography>
          <Typography sx={statSubSx}>
            {plan.trial
              ? 'does not renew'
              : plan.autoRenew
                ? `${plan.renewalTotal != null ? `${inr(plan.renewalTotal)} incl. GST` : 'auto-renew on'}${plan.nextPlanName ? `, on ${plan.nextPlanName}` : ''}`
                : 'expires, no renewal'}
          </Typography>
        </Grid>
      </Grid>

      <Box sx={{ mb: 1.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
          <Typography sx={smallSx}>
            {inOverage
              ? `Plan used up — ${overage.toLocaleString('en-IN')} shipments over`
              : `${percent}% of your included shipments used`}
          </Typography>
          {inOverage && (
            <Typography sx={{ ...strongSx, color: WARNING }}>
              {inr(overageCost)} over-usage so far —{' '}
              <Box component='span' onClick={onChangePlan} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                upgrade
              </Box>{' '}
              to stop it
            </Typography>
          )}
        </Box>
        <LinearProgress
          variant='determinate'
          value={percent}
          sx={{
            height: 7,
            borderRadius: 4,
            bgcolor: LIGHT_BG,
            '& .MuiLinearProgress-bar': { bgcolor: inOverage ? WARNING : SUCCESS },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.lg, flexWrap: 'wrap', pt: 1.25, borderTop: `1px solid ${BORDER}` }}>
        {plan.trial ? (
          /* A trial never renews and is once per account: the way on is choosing a plan. */
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
            <Box>
              <Typography sx={strongSx}>Free trial</Typography>
              <Typography sx={smallSx}>
                Ends on {renewOn ? formatDate(renewOn, 'dd MMM') : 'the end of the period'} and does not renew
              </Typography>
            </Box>
            <Button size='small' sx={secondaryBtnSx} onClick={onChangePlan}>Choose a plan</Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
            <Box>
              <Typography sx={strongSx}>Auto-renew</Typography>
              <Typography sx={smallSx}>
                {plan.autoRenew
                  ? `${plan.renewalTotal != null ? inr(plan.renewalTotal) : 'Charged'} to your ${plan.paidFrom === 'POSTPAID' ? 'invoice' : 'wallet'} on ${renewOn ? formatDate(renewOn, 'dd MMM') : 'renewal'}`
                  : 'The plan will expire at the end of this period'}
              </Typography>
            </Box>
            <Switch size='small' checked={Boolean(plan.autoRenew)} onChange={(e) => onAutoRenew(e.target.checked)} />
          </Box>
        )}

        <Box sx={{ flex: 1 }} />
        <Divider orientation='vertical' flexItem sx={{ borderColor: BORDER, display: { xs: 'none', md: 'block' } }} />

        {/*
          The shortcut for "I only want to ship on my own contracts". It is the same action as
          switching every ProShip courier off in Couriers, surfaced here because this is the page a
          merchant lands on after activating and it is the decision they are most likely to want.
        */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm }}>
          <Box>
            <Typography sx={strongSx}>Use ProShip couriers</Typography>
            <Typography sx={smallSx}>
              {proship.enabled > 0
                ? `${proship.enabled} of ${proship.total} available alongside yours`
                : 'All switched off — only your couriers are used'}
            </Typography>
          </Box>
          <Tooltip
            title={
              proship.enabled > 0
                ? 'Switching this off disables every ProShip courier. Only your own couriers will be allocated, which may reduce the pincodes you can serve.'
                : 'Switch on to make ProShip couriers available for allocation again.'
            }
          >
            <span>
              <Switch
                size='small'
                checked={proship.enabled > 0}
                disabled={proship.total === 0}
                onChange={(e) => onToggleProship(e.target.checked)}
              />
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
